import { PackageCheck, Truck, Bell } from 'lucide-react';
import { LIEFERWUNSCH_LABEL, LIEFERWUNSCH_VORGABE } from '@/lib/lieferwunsch';
import type { Lieferwunsch } from '@/types/detail';

/**
 * Der Lieferwunsch in der Bestellung (A56, #135).
 *
 * WARUM ER HIER STEHT UND NICHT IN EINEM AUFKLAPPBEREICH
 *   Er muss sichtbar sein, wo entschieden wird, ob etwas rausgeht — in
 *   der Reihe unter dem Kopf, neben Zustand, Versandart und Termin. Wer
 *   am Packtisch überlegt, ob die eine eingetroffene Position schon
 *   losgeschickt wird, soll den Wunsch sehen, ohne ihn zu suchen.
 *
 *   Bei der ERFASSUNG ist das anders: Dort steht er im zurückhaltenden
 *   Bereich, weil er selten gesetzt wird und das Formular nicht
 *   aufblähen soll. Hier wird er gelesen, dort geschrieben.
 *
 * ZWEI WERTE, EIN KNOPF
 *   Es gibt genau zwei Seiten; ein Knopf, der zwischen ihnen wechselt,
 *   ist kürzer als eine Auswahl und trägt seinen Zustand im Text.
 *
 * KEINE RÜCKFRAGE: Es ist eine Angabe, kein Vorgang, und sofort wieder
 * umschaltbar — wie beim Geschenkwunsch nebenan.
 */
export function LieferwunschSchalter({
  wert,
  nachrichtWunsch,
  onChange
}: {
  wert: Lieferwunsch;
  /** „Sagt mir Bescheid, wenn alles da ist" — steht als Zusatz daneben. */
  nachrichtWunsch: boolean;
  onChange: (neu: Lieferwunsch) => void;
}) {
  const sofort = wert === 'sofort';
  const Icon = sofort ? Truck : PackageCheck;
  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        type="button"
        aria-pressed={sofort}
        onClick={() => onChange(sofort ? 'komplett' : 'sofort')}
        className={
          'inline-flex min-h-[44px] items-center gap-1.5 text-sm transition-colors ' +
          (wert === LIEFERWUNSCH_VORGABE
            ? 'text-ink-soft hover:text-akzent'
            : 'text-ink hover:text-akzent')
        }
        title={
          sofort
            ? 'Die Kundin nimmt Teillieferungen — antippen, um auf „erst wenn alles da ist" zu wechseln'
            : 'Erst wenn alles da ist — antippen, wenn die Kundin Teillieferungen möchte'
        }
      >
        <Icon aria-hidden className={'h-4 w-4 shrink-0 ' + (sofort ? 'text-akzent' : '')} />
        {LIEFERWUNSCH_LABEL[wert]}
      </button>
      {/* Der Nachrichtenwunsch hat keinen eigenen Griff in dieser Reihe:
          Er wird selten geändert und steht in der Checkliste ausführlich.
          Hier ist er ein Zeichen, das sagt „da war etwas". */}
      {nachrichtWunsch && (
        <span
          className="inline-flex shrink-0 items-center gap-1 rounded bg-akzent-sanft px-1.5 py-0.5 text-xs text-akzent-stark"
          title="Die Kundin wünscht eine Nachricht, sobald alles da ist"
        >
          <Bell aria-hidden className="h-3 w-3" />
          Nachricht
        </span>
      )}
    </span>
  );
}
